import { useTranslation } from 'react-i18next'

import { QA_NAME_CANCEL_BTN, QA_NAME_SAVE_BTN } from '../../../constants/qa'
import { SIZE } from '../../../constants/size'
import { useScrollToInlineNotifications } from '../../../hooks'
import { getHasFilesToShow } from '../../../utils/file'
import Button from '../../Button'
import { FieldStackedLabel } from '../../Field'
import { FileAttachTag, FileList } from '../../File'
import {
  FormModalConfirmation,
  FormUploadAction,
  useCloseConfirm,
} from '../../Form'
import Modal, { Box, Buttons, Content, Header } from '../../Modal'
import { NotificationInlineList } from '../../Notification'
import { TAG_HEIGHT } from '../../Tag'
import { TextareaInlineField } from '../../Textarea'

import {
  QA_NAME_EDIT_RATING_MODAL,
  RATING_EDIT_MODAL_MAX_COMMENT_LENGTH,
  RATING_TYPE_CONTRACTOR,
  RATING_TYPE_MANAGEMENT_COMPANY,
  RATING_TYPE_OVERALL,
} from './RatingEditModal.constants'
import { useUpdateRating } from './RatingEditModal.hooks'
import { RatingEditModalItem } from './RatingEditModalItem'

import * as styles from './RatingEditModal.module.css'

function RatingEditModal({
  ratingData,
  requestId,
  withComment,
  withFiles,
  onClose,
  onUpdateListData,
}) {
  const { t } = useTranslation()

  const {
    feedbackText,
    files,
    filesError,
    filesUploading,
    handleChangeFeedbackText,
    handleChangeRating,
    handleDeleteFile,
    handleSave,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
    isDescriptionEditingDisabled,
    isFeedbackChanged,
    isLoading,
    isRatingChanged,
    isRatingEditingDisabled,
    notifications,
    rating,
  } = useUpdateRating({
    ratingData,
    requestId,
    onClose,
    onUpdateListData,
  })

  const { handleClose, handleCloseConfirmation, isCloseConfirm } =
    useCloseConfirm({ onClose })

  const { ref: notificationsRef, setScrollToNotifications } =
    useScrollToInlineNotifications()

  function handleClickSave() {
    handleSave()
    setScrollToNotifications(prev => prev + 1)
  }

  function handleRemoveFile(file) {
    handleDeleteFile(file.id)
  }

  function renderFile(file, key) {
    return (
      <FileAttachTag
        file={file}
        height={TAG_HEIGHT.height40}
        key={key}
        onRemove={isFeedbackEditingDisabled ? undefined : handleRemoveFile}
      />
    )
  }

  function handleCloseWithConfirm() {
    handleClose({ dirty: isFeedbackChanged || isRatingChanged })
  }

  const showFiles =
    withFiles && getHasFilesToShow({ files, filesError, filesUploading })

  const hasFeedback = Boolean(ratingData?.feedback?.uuid)

  const hasOverallRating = Boolean(rating[RATING_TYPE_OVERALL]?.value)

  const isFeedbackEditingDisabled =
    (!hasOverallRating && !hasFeedback) || isDescriptionEditingDisabled

  return (
    <>
      <Modal
        qaName={QA_NAME_EDIT_RATING_MODAL}
        onClose={handleCloseWithConfirm}
      >
        <Box size={SIZE.LARGE}>
          <Header
            text={t(
              hasFeedback
                ? 'Modal.EditRatingTitleKey'
                : 'Modal.AddRatingTitleKey'
            )}
            onClose={handleCloseWithConfirm}
          />
          <Content className={styles.content}>
            {notifications.length > 0 ? (
              <NotificationInlineList
                className={styles.notifications}
                items={notifications}
                ref={notificationsRef}
              />
            ) : null}
            <RatingEditModalItem
              isRequired
              disabled={isRatingEditingDisabled}
              rating={rating[RATING_TYPE_OVERALL]?.value}
              type={RATING_TYPE_OVERALL}
              onClick={handleChangeRating}
            />
            <RatingEditModalItem
              disabled={!hasOverallRating || isRatingEditingDisabled}
              rating={rating[RATING_TYPE_MANAGEMENT_COMPANY]?.value}
              type={RATING_TYPE_MANAGEMENT_COMPANY}
              onClick={handleChangeRating}
            />
            <RatingEditModalItem
              disabled={!hasOverallRating || isRatingEditingDisabled}
              rating={rating[RATING_TYPE_CONTRACTOR]?.value}
              type={RATING_TYPE_CONTRACTOR}
              onClick={handleChangeRating}
            />
            {withComment ? (
              <FieldStackedLabel
                className={styles.field}
                disabled={isFeedbackEditingDisabled}
                label={t('Rating.CommentLabelKey')}
              >
                <TextareaInlineField
                  autoResizeHeight
                  withoutManualResize
                  disabled={isFeedbackEditingDisabled}
                  maxLength={RATING_EDIT_MODAL_MAX_COMMENT_LENGTH}
                  placeholder={t('Rating.CommentPlaceholderKey')}
                  value={feedbackText}
                  onChange={handleChangeFeedbackText}
                />
              </FieldStackedLabel>
            ) : null}
            {showFiles ? (
              <FieldStackedLabel
                className={styles.field}
                label={t('Modal.AttachedFilesTitleKey')}
              >
                <FileList
                  className={styles.files}
                  errorItems={filesError}
                  files={files}
                  itemClassName={styles.fileItem}
                  renderItem={renderFile}
                  uploadingItems={filesUploading}
                />
              </FieldStackedLabel>
            ) : null}
          </Content>
          <Buttons>
            <Button.Contained
              isLoading={isLoading}
              qaName={QA_NAME_SAVE_BTN}
              text={t('Modal.SaveBtnKey')}
              onClick={handleClickSave}
            />
            <Button.Outlined
              qaName={QA_NAME_CANCEL_BTN}
              text={t('Modal.CancelBtnKey')}
              onClick={handleCloseWithConfirm}
            />
            {withFiles ? (
              <FormUploadAction
                disabled={isFeedbackEditingDisabled}
                onFinishUpload={handleUploadFile}
                onStartUpload={handleStartUploading}
                onUploadErrors={handleUploadErrors}
              />
            ) : null}
          </Buttons>
        </Box>
      </Modal>
      {isCloseConfirm ? (
        <FormModalConfirmation
          onCancel={handleCloseConfirmation}
          onSubmit={onClose}
        />
      ) : null}
    </>
  )
}

export default RatingEditModal
